import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import workoutRecordsService from '../../../services/workoutRecordsService';

const METRIC_OPTIONS = [
  { value: 'weight', label: 'Weight (kg)', color: '#6366f1' },
  { value: 'reps', label: 'Reps', color: '#10b981' },
  { value: 'sets', label: 'Sets', color: '#f59e0b' },
];

const SuggestionCard = ({ suggestion }) => {
  if (!suggestion) return null;
  return (
    <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-xl">
      <div className="flex items-center justify-center w-9 h-9 bg-primary/10 rounded-lg flex-shrink-0 mt-0.5">
        <Icon name="TrendingUp" size={18} className="text-primary" />
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground mb-0.5">Next Session Target</p>
        <p className="text-xs text-muted-foreground mb-2">{suggestion?.note}</p>
        <div className="flex flex-wrap gap-2">
          {suggestion?.sets > 0 && (
            <span className="text-xs font-medium bg-card border border-border/40 px-2.5 py-1 rounded-full text-foreground">
              {suggestion?.sets} sets
            </span>
          )}
          {suggestion?.reps > 0 && (
            <span className="text-xs font-medium bg-card border border-border/40 px-2.5 py-1 rounded-full text-foreground">
              {suggestion?.reps} reps
            </span>
          )}
          {suggestion?.weight > 0 && (
            <span className="text-xs font-medium bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full text-primary font-semibold">
              {suggestion?.weight} kg
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border/40 rounded-lg p-3 shadow-wellness text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload?.map((entry) => (
        <p key={entry?.dataKey} style={{ color: entry?.color }}>
          {entry?.name}: <span className="font-bold">{entry?.value}</span>
        </p>
      ))}
    </div>
  );
};

const ProgressiveOverloadTracker = ({ userId, onNewPR }) => {
  const [exerciseNames, setExerciseNames] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [newExerciseInput, setNewExerciseInput] = useState('');
  const [history, setHistory] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [activeMetric, setActiveMetric] = useState('weight');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newPRBroken, setNewPRBroken] = useState([]);

  const [form, setForm] = useState({
    exerciseName: '',
    sets: '',
    reps: '',
    weight: '',
    date: new Date()?.toISOString()?.split('T')?.[0],
    notes: '',
  });

  // Load exercise names
  useEffect(() => {
    if (!userId) return;
    workoutRecordsService?.getExerciseNames(userId)?.then(names => {
      setExerciseNames(names);
      if (names?.length > 0 && !selectedExercise) {
        setSelectedExercise(names?.[0]);
      }
    })?.catch(console.error);
  }, [userId]);

  // Load history when exercise changes
  const loadHistory = useCallback(async (exerciseName) => {
    if (!userId || !exerciseName) return;
    setLoading(true);
    try {
      const data = await workoutRecordsService?.getExerciseProgression(userId, exerciseName);
      setHistory(data);
      const sugg = workoutRecordsService?.getSuggestion(data);
      setSuggestion(sugg);
    } catch (err) {
      console.error('Error loading progression:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (selectedExercise) {
      loadHistory(selectedExercise);
      setForm(prev => ({ ...prev, exerciseName: selectedExercise }));
    }
  }, [selectedExercise, loadHistory]);

  const chartData = history?.map(r => ({
    date: new Date(r?.workout_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    weight: r?.weight || 0,
    reps: r?.reps || 0,
    sets: r?.sets || 0,
  }));

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const exerciseName = form?.exerciseName?.trim() || newExerciseInput?.trim();
    if (!exerciseName) return;
    if (!form?.sets && !form?.reps && !form?.weight) return;

    setSaving(true);
    setNewPRBroken([]);
    try {
      const recordData = {
        exerciseName,
        sets: parseInt(form?.sets) || 0,
        reps: parseInt(form?.reps) || 0,
        weight: parseFloat(form?.weight) || 0,
        date: form?.date,
        notes: form?.notes,
      };

      // Check for PRs before saving
      const broken = await workoutRecordsService?.checkForNewPR(userId, exerciseName, recordData);
      await workoutRecordsService?.logWorkoutRecord(userId, recordData);

      if (broken?.length > 0) {
        setNewPRBroken(broken);
        onNewPR && onNewPR(exerciseName, broken);
      }

      // Refresh exercise list and history
      const names = await workoutRecordsService?.getExerciseNames(userId);
      setExerciseNames(names);
      setSelectedExercise(exerciseName);
      setNewExerciseInput('');
      setShowForm(false);
      setForm(prev => ({ ...prev, sets: '', reps: '', weight: '', notes: '' }));
    } catch (err) {
      console.error('Error saving workout record:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-wellness elevation-1 border border-border/40 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border/40">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="TrendingUp" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Progressive Overload</h3>
              <p className="text-sm text-muted-foreground">Track sets, reps & weight over time</p>
            </div>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => setShowForm(v => !v)}
            iconName={showForm ? 'X' : 'Plus'}
            iconPosition="left"
          >
            {showForm ? 'Cancel' : 'Log Workout'}
          </Button>
        </div>
      </div>
      <div className="p-6 space-y-5">
        {/* PR Celebration */}
        <AnimatePresence>
          {newPRBroken?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl"
            >
              <Icon name="Trophy" size={22} className="text-yellow-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-yellow-600 dark:text-yellow-400">🎉 New Personal Record!</p>
                <p className="text-xs text-muted-foreground">
                  You broke your PR in: {newPRBroken?.join(', ')}
                </p>
              </div>
              <button onClick={() => setNewPRBroken([])} className="ml-auto text-muted-foreground hover:text-foreground">
                <Icon name="X" size={14} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Log Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 bg-muted/20 rounded-xl border border-border/30 space-y-4">
                <p className="text-sm font-semibold text-foreground">Log New Session</p>

                {/* Exercise selector */}
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Exercise</label>
                  {exerciseNames?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {exerciseNames?.map(name => (
                        <button
                          key={name}
                          onClick={() => { setSelectedExercise(name); setForm(prev => ({ ...prev, exerciseName: name })); setNewExerciseInput(''); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors duration-150 ${
                            form?.exerciseName === name
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-card text-foreground border-border/40 hover:border-primary/40'
                          }`}
                        >
                          {name}
                        </button>
                      ))}
                      <button
                        onClick={() => { setForm(prev => ({ ...prev, exerciseName: '' })); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors duration-150 ${
                          !form?.exerciseName
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-card text-foreground border-border/40 hover:border-primary/40'
                        }`}
                      >
                        + New
                      </button>
                    </div>
                  ) : null}

                  {(!form?.exerciseName || exerciseNames?.length === 0) && (
                    <input
                      type="text"
                      placeholder="Exercise name (e.g. Bench Press)"
                      value={newExerciseInput}
                      onChange={e => setNewExerciseInput(e?.target?.value)}
                      className="w-full px-3 py-2 text-sm bg-card border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  )}
                </div>

                {/* Sets / Reps / Weight */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Sets</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="3"
                      value={form?.sets}
                      onChange={e => handleFormChange('sets', e?.target?.value)}
                      className="w-full px-3 py-2 text-sm bg-card border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Reps</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="10"
                      value={form?.reps}
                      onChange={e => handleFormChange('reps', e?.target?.value)}
                      className="w-full px-3 py-2 text-sm bg-card border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Weight (kg)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="60"
                      value={form?.weight}
                      onChange={e => handleFormChange('weight', e?.target?.value)}
                      className="w-full px-3 py-2 text-sm bg-card border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>

                {/* Date + Notes */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Date</label>
                    <input
                      type="date"
                      value={form?.date}
                      onChange={e => handleFormChange('date', e?.target?.value)}
                      className="w-full px-3 py-2 text-sm bg-card border border-border/40 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label>
                    <input
                      type="text"
                      placeholder="Optional notes"
                      value={form?.notes}
                      onChange={e => handleFormChange('notes', e?.target?.value)}
                      className="w-full px-3 py-2 text-sm bg-card border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>

                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || (!form?.exerciseName && !newExerciseInput?.trim())}
                  iconName={saving ? undefined : 'Save'}
                  iconPosition="left"
                  className="w-full"
                >
                  {saving ? 'Saving...' : 'Save Session'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Exercise Selector (when not in form mode) */}
        {!showForm && exerciseNames?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {exerciseNames?.map(name => (
              <button
                key={name}
                onClick={() => setSelectedExercise(name)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors duration-150 ${
                  selectedExercise === name
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/30 text-foreground border-border/40 hover:border-primary/40'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!showForm && exerciseNames?.length === 0 && (
          <div className="text-center py-8">
            <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mx-auto mb-3">
              <Icon name="Dumbbell" size={28} className="text-primary/50" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">No workouts logged yet</p>
            <p className="text-xs text-muted-foreground">Click "Log Workout" to start tracking your progressive overload.</p>
          </div>
        )}

        {/* Chart + Suggestion */}
        {!showForm && selectedExercise && (
          <>
            {/* Metric toggle */}
            <div className="flex gap-2">
              {METRIC_OPTIONS?.map(m => (
                <button
                  key={m?.value}
                  onClick={() => setActiveMetric(m?.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors duration-150 ${
                    activeMetric === m?.value
                      ? 'border-primary/50 text-primary bg-primary/10' :'border-border/40 text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  {m?.label}
                </button>
              ))}
            </div>

            {/* Chart */}
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : chartData?.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey={activeMetric}
                      name={METRIC_OPTIONS?.find(m => m?.value === activeMetric)?.label}
                      stroke={METRIC_OPTIONS?.find(m => m?.value === activeMetric)?.color}
                      strokeWidth={2.5}
                      dot={{ r: 4, fill: METRIC_OPTIONS?.find(m => m?.value === activeMetric)?.color }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">
                No data yet for <span className="font-medium ml-1">{selectedExercise}</span>
              </div>
            )}

            {/* Suggestion */}
            {suggestion && <SuggestionCard suggestion={suggestion} />}
          </>
        )}
      </div>
    </div>
  );
};

export default ProgressiveOverloadTracker;
